import _extends from "@babel/runtime/helpers/extends";
import React, { useContext, useMemo } from 'react';
import * as NerdWalletReactAnalytics from '@nerdwallet/analytics/react/impression';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import MarketplaceApplyNow, { APPLY_NOW_LINK_TYPES, LINK_TYPES, APPLY_NOW_FINISH_TYPES } from '@nerdwallet/react-marketplace-apply-now';
import { getLegacyId } from '@nerdwallet/marketplace/selectors/credit-cards';
import BaseProductCtas from './base-product-ctas';
import { TRK_PRODUCT_LOCATION, COMPONENT_ANALYTICS_NAME } from '../lib/constants';
import { buildTrkSectionCategory, getMonetizable } from '../lib/utils';
import styles from "../marketplace-product-card.module.css";
import AddToCompare, { AddToCompareCtaEnum } from './add-to-compare';

/**
 * @deprecated
 */
var MappedProductCtas = function MappedProductCtas(_ref) {
  var _handleCcCompareClick = _ref._handleCcCompareClick,
      _hasCcCompareButton = _ref._hasCcCompareButton,
      analytics = _ref.analytics,
      className = _ref.className,
      ctaHref = _ref.ctaHref,
      ctaShowIcon = _ref.ctaShowIcon,
      ctaSubtext = _ref.ctaSubtext,
      ctaText = _ref.ctaText,
      _ref$displayPage = _ref.displayPage,
      displayPage = _ref$displayPage === void 0 ? '' : _ref$displayPage,
      enableAnalytics = _ref.enableAnalytics,
      id = _ref.id,
      marketplaceEntity = _ref.marketplaceEntity,
      reviewLinkHref = _ref.reviewLinkHref,
      secondaryCtaHref = _ref.secondaryCtaHref,
      secondaryCtaText = _ref.secondaryCtaText,
      shouldLogImpression = _ref.shouldLogImpression,
      taxonomy = _ref.taxonomy,
      trkPageType = _ref.trkPageType,
      trkProductPosition = _ref.trkProductPosition,
      trkSectionCategory = _ref.trkSectionCategory,
      _ccCompareButtonTextBeforeClick = _ref._ccCompareButtonTextBeforeClick,
      _ccCompareButtonTextAfterClick = _ref._ccCompareButtonTextAfterClick;
  useDeprecatedPropsWarning({
    component: 'ProductCtas',
    deprecatedProps: [{
      version: '6.9.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics prop instead.'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });
  var impressionContext = useContext(NerdWalletReactAnalytics.ImpressionContext);
  var impressionId = impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId;
  var passThroughProps = {
    analytics: analytics,
    className: className
  };
  var addToCompare = useMemo(function () {
    if (!_hasCcCompareButton) {
      return null;
    }

    return /*#__PURE__*/React.createElement(AddToCompare, {
      ccCompareButtonTextAfterClick: _ccCompareButtonTextAfterClick,
      ccCompareButtonTextBeforeClick: _ccCompareButtonTextBeforeClick,
      ccCompareCtaType: AddToCompareCtaEnum.button,
      handleCcCompareClick: _handleCcCompareClick,
      isAnalyticsEnabled: !!analytics,
      legacyCreditCardId: getLegacyId(marketplaceEntity)
    });
  }, [_hasCcCompareButton, _ccCompareButtonTextAfterClick, _ccCompareButtonTextBeforeClick, _handleCcCompareClick, analytics, marketplaceEntity]);
  return /*#__PURE__*/React.createElement(BaseProductCtas, _extends({}, passThroughProps, {
    addToCompareNode: addToCompare,
    primaryCtaNode: /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
      annotationText: ctaSubtext,
      className: styles.productLink,
      disableAnnotation: ctaSubtext === false,
      id: id,
      impressionId: impressionId,
      link: ctaHref,
      linkType: APPLY_NOW_LINK_TYPES.applyNowButton,
      marketplaceEntity: marketplaceEntity,
      monetizable: getMonetizable(marketplaceEntity),
      productLocation: TRK_PRODUCT_LOCATION // TODO stop passing this through
      ,
      productPosition: trkProductPosition,
      reviewLink: reviewLinkHref,
      sectionName: (analytics === null || analytics === void 0 ? void 0 : analytics.sectionName) || COMPONENT_ANALYTICS_NAME,
      shouldLogImpression: shouldLogImpression,
      showIcon: ctaShowIcon,
      text: ctaText,
      trkPageType: trkPageType,
      trkSectionCategory: buildTrkSectionCategory(trkSectionCategory, 'cta'),
      trkTaxonomy: taxonomy,
      type: LINK_TYPES.BUTTON // BNK specific props.
      ,
      displayPage: displayPage
    }),
    secondaryCtaNode: !!secondaryCtaText && /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
      className: styles.secondaryCta,
      finishType: APPLY_NOW_FINISH_TYPES.EXTERNAL_CONTENT,
      impressionId: impressionId,
      id: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
      link: secondaryCtaHref,
      linkType: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
      marketplaceEntity: marketplaceEntity,
      monetizable: getMonetizable(marketplaceEntity),
      sectionName: (analytics === null || analytics === void 0 ? void 0 : analytics.sectionName) || COMPONENT_ANALYTICS_NAME,
      sectionPosition: analytics === null || analytics === void 0 ? void 0 : analytics.sectionPosition,
      type: LINK_TYPES.LINK,
      shouldLogImpression: shouldLogImpression
    }, secondaryCtaText)
  }));
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  var PropTypes = require('prop-types'); // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires


  var _require = require('../prop-types'),
      AnalyticsPropType = _require.AnalyticsPropType,
      TRKPropTypes = _require.TRKPropTypes;

  MappedProductCtas.propTypes = {
    analytics: AnalyticsPropType,
    className: PropTypes.string,
    ctaHref: PropTypes.string,
    ctaShowIcon: PropTypes.bool,
    ctaSubtext: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    ctaText: PropTypes.string,

    /* @deprecated */
    enableAnalytics: PropTypes.bool,
    id: PropTypes.string,
    marketplaceEntity: PropTypes.object.isRequired,
    // eslint-disable-line react/forbid-prop-types
    reviewLinkHref: PropTypes.string,
    secondaryCtaHref: PropTypes.string,
    secondaryCtaText: PropTypes.string,
    shouldLogImpression: PropTypes.bool,
    taxonomy: TRKPropTypes,
    trkPageType: PropTypes.string,
    trkProductPosition: PropTypes.number,
    trkSectionCategory: PropTypes.string,
    // BNK specific props.
    displayPage: PropTypes.string,
    // CC specific props
    _hasCcCompareButton: PropTypes.bool,
    _handleCcCompareClick: PropTypes.func,
    _ccCompareButtonTextBeforeClick: PropTypes.string,
    // custom compare button text prop
    _ccCompareButtonTextAfterClick: PropTypes.string
  };
}

MappedProductCtas.defaultProps = {
  // BNK specific props.
  displayPage: '',
  // CC specific props
  _hasCcCompareButton: false,
  _handleCcCompareClick: function _handleCcCompareClick() {}
};
export default MappedProductCtas;